import { useTranslation } from "react-i18next";

import type { ChartMeasure } from "@/lib/calls-chart";
import { cn } from "@/lib/utils";

/** One choice of a `<SegmentedControl>`. */
export interface Segment<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  segments: readonly Segment<T>[];
  value: T;
  onChange: (value: T) => void;
  /** The choice's name for screen readers: "Weergave". */
  label: string;
  className?: string;
}

/**
 * A choice between a few views of the same thing, as one bordered row of
 * buttons with the chosen one in De Ceuster blue (`aria-pressed`), such as
 * "Aantal | Percentage" above a chart of failures (`<MeasureSwitch>`).
 *
 *     <SegmentedControl
 *       label="Weergave"
 *       segments={[{ value: "list", label: "Lijst" }, { value: "chart", label: "Grafiek" }]}
 *       value={view}
 *       onChange={setView}
 *     />
 */
export function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  label,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("border-input bg-card flex w-fit rounded-md border p-0.5", className)}
    >
      {segments.map((segment) => (
        <button
          key={segment.value}
          type="button"
          aria-pressed={segment.value === value}
          onClick={() => onChange(segment.value)}
          className={cn(
            "focus-visible:ring-ring ring-offset-background h-7.5 rounded-sm px-3 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none pointer-coarse:h-8.5",
            segment.value === value
              ? "bg-primary text-primary-foreground"
              : "text-foreground hover:bg-accent-light hover:text-primary",
          )}
        >
          {segment.label}
        </button>
      ))}
    </div>
  );
}

interface MeasureSwitchProps {
  value: ChartMeasure;
  onChange: (measure: ChartMeasure) => void;
  className?: string;
}

/**
 * "Aantal | Percentage" for a chart of failures: the failed calls as a
 * count, or as a percentage of that day's calls (`<CallsChart
 * series="errors" measure>`).
 */
export function MeasureSwitch({ value, onChange, className }: MeasureSwitchProps) {
  const { t } = useTranslation();
  return (
    <SegmentedControl
      label={t("chart.measure.label")}
      segments={[
        { value: "count", label: t("chart.measure.count") },
        { value: "percent", label: t("chart.measure.percent") },
      ]}
      value={value}
      onChange={onChange}
      className={className}
    />
  );
}
